import { Stack } from "@mantine/core";
import { IComment, IEditorMetadata, IUserDepricated } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import React, { useState } from "react";
import { Prompt } from "react-router";
import { Button, UncontrolledTooltip } from "reactstrap";
import { CreateNewButton, HSpace, MCLoader, McTitle, SingleComment } from "..";
import NewsSearch from "../../app/routes/projectdashboard/News/NewsSearch";
import { sortArrayByDate } from "../../app/utils";
import { setHasUnsavedCommentInRedux } from "../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { SendIcon } from "../CustomIcons/CustomIcons";
import Editor, { lexicalStateToPlainText } from "../RichTextEditor/RichTextEditor";

type ICommentWithReactions = IComment & {
  reactions?: { user: { _id: string; name: string }; date: string; emoji: string }[];
};

const MultiComment: React.FC<{
  comments: ICommentWithReactions[];
  title?: string;
  onSave: (commentId: string, text: string, metadata: IEditorMetadata) => void;
  onDelete: (commentId: string) => void;
  onCreate: (text: string, metadata: IEditorMetadata) => void;
  showSearch?: boolean;
  subTitle?: string;
  sortDesc?: boolean;
  loggedInUser?: IUserDepricated;
  noCommentsText?: string;
  members?: IProjectMemberApiItem[];
  allowDelete?: boolean;
  loading?: boolean;
  visibleCommentsOnLoad?: number;
  useBrowserPrompt?: boolean;
  onReact?: (commentId: string, emoji: string) => void;
  currentUserId?: string;
}> = ({
  comments,
  title = "",
  onSave,
  onDelete,
  onCreate,
  showSearch = false, //only useful for now in News, not for comments
  subTitle,
  sortDesc = true,
  loggedInUser,
  noCommentsText,
  members,
  allowDelete = false,
  loading = false,
  visibleCommentsOnLoad = 5,
  useBrowserPrompt = true,
  onReact,
  currentUserId,
}) => {
  const filteredComments = comments.filter((item) => item.deleted === false);
  const [addNewEditor, setAddNewEditor] = useState(false);
  const [doShowAll, setDoShowAll] = useState(false);
  const sortedComments = sortArrayByDate(filteredComments, "createdDate", sortDesc);
  const commentsToShow = doShowAll ? sortedComments : sortedComments.slice(0, visibleCommentsOnLoad);
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);
  const onLocationChange = () => {
    return "Du har en ulagret kommentar. Er du sikker på at du vil avbryte?";
  };

  return (
    <div className="d-flex flex-column w-100 h-100">
      {title != null && (
        <McTitle title={title}>
          {subTitle != null && <h6 className="gray-400-text">{subTitle}</h6>}
          {addNewEditor === false && (
            <div className="d-flex">
              <CreateNewButton size="xs" onClick={() => setAddNewEditor(true)} outline />
              <div className="flex-fill d-flex justify-content-end">{showSearch && <NewsSearch />}</div>
            </div>
          )}
          {useBrowserPrompt === true && <Prompt when={hasUnsavedComment === true} message={onLocationChange} />}
          <AddNewComment
            hideAddButton={true}
            members={members}
            onAddComment={(text: string, metadata: IEditorMetadata) => onCreate(text, metadata)}
            addNewEditor={addNewEditor}
            onToggleAddNewEditor={(state) => setAddNewEditor(state)}
          />
          <Stack gap="md" className="pl-2">
            <MCLoader loading={loading} />
            {filteredComments.length === 0 && loading !== true && filteredComments.length === 0 && (
              <div className="text-center mt-3">{noCommentsText}</div>
            )}
            {commentsToShow.map((comment) => (
              <SingleComment
                key={comment._id}
                comment={comment}
                editEnabled={
                  loggedInUser?._id === (comment.createdBy as IUserDepricated)?._id ||
                  loggedInUser?._id === comment.createdBy
                }
                deleteEnabled={
                  loggedInUser?._id === (comment.createdBy as IUserDepricated)?._id ||
                  loggedInUser?._id === comment.createdBy ||
                  allowDelete
                }
                onSave={(text, metadata) => onSave(comment._id, text, metadata)}
                onDelete={() => onDelete(comment._id)}
                members={members}
                reactions={comment.reactions}
                onReact={onReact ? (emoji) => onReact(comment._id, emoji) : undefined}
                currentUserId={currentUserId}
              />
            ))}
            {sortedComments.length > visibleCommentsOnLoad && doShowAll === false && (
              <Button color="secondary" onClick={() => setDoShowAll(true)}>
                <span className="mx-2">
                  {"Vis alt"} <i className="ml-2 fa fa-angle-down"></i>
                </span>
              </Button>
            )}
          </Stack>
        </McTitle>
      )}
    </div>
  );
};

export default MultiComment;

export const AddNewComment: React.FC<{
  onAddComment: (text: string, IEditorMetadata) => void;
  positionRight?: boolean;
  hideAddButton?: boolean;
  members?: IProjectMemberApiItem[];
  addNewEditor: boolean;
  onToggleAddNewEditor: (state: boolean) => void; // Add this prop to allow parent to control addNew state
}> = ({ onAddComment, positionRight, hideAddButton = false, members, addNewEditor, onToggleAddNewEditor }) => {
  const [editorState, setEditorState] = useState("");
  const [plainText, setPlainText] = useState("");
  const [editorMetadata, setEditorMetadata] = useState<IEditorMetadata>(null);
  const [hasImages, setHasImages] = useState(false);
  const dispatch = useAppDispatch();
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);

  const abort = () => {
    onToggleAddNewEditor(false);
    setEditorState("");
    dispatch(setHasUnsavedCommentInRedux(false));
  };

  const add = async () => {
    onToggleAddNewEditor(false);
    const plainText = await lexicalStateToPlainText(editorState);
    onAddComment(JSON.stringify(editorState), { ...editorMetadata, plainText: plainText });
    setEditorState("");
    setEditorMetadata(null);
    dispatch(setHasUnsavedCommentInRedux(false));
  };

  const handleChange = (editorState, plainText, hasImages) => {
    setEditorState(editorState);
    setPlainText(plainText);
    setHasImages(hasImages);
    if (plainText.length > 0 && hasUnsavedComment !== true) {
      dispatch(setHasUnsavedCommentInRedux(true));
    } else if (plainText.length < 1) {
      dispatch(setHasUnsavedCommentInRedux(false));
    }
  };
  return (
    <div className="w-100 mb-1">
      {addNewEditor === false && hideAddButton === false && (
        <div className={`${positionRight ? "d-flex justify-content-end" : ""} `}>
          <Button size="sm" color="success" outline onClick={() => onToggleAddNewEditor(true)}>
            <i className="fa fa-plus fa-fw" />
          </Button>
        </div>
      )}
      {addNewEditor === true && (
        <>
          <div className="d-flex w-100">
            <Editor
              members={members}
              onChange={handleChange}
              handleMentions={(e) => setEditorMetadata({ ...editorMetadata, mentionIdArray: e })}
            />
          </div>
          <div className="d-flex mt-2">
            <Button
              className="d-flex align-items-center"
              id="saveButton"
              disabled={plainText.length < 1 && hasImages === false}
              onClick={() => add()}
              color="success"
              size="sm"
            >
              Send
              <SendIcon />
            </Button>
            <UncontrolledTooltip target="saveButton" delay={{ show: 350, hide: 100 }}>
              Send
            </UncontrolledTooltip>
            <HSpace />
            <Button id="closeButton" onClick={() => abort()} size="sm">
              <i className="fa fa-close fa-fw" />
            </Button>
            <UncontrolledTooltip target="closeButton" delay={{ show: 350, hide: 100 }}>
              Avbryt
            </UncontrolledTooltip>
          </div>
        </>
      )}
    </div>
  );
};
